import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Fab,
  alpha,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import ProjectCard from 'src/components/ProjectCard';
import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import ProjectDialog from './ProjectDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Project {
  id: string;
  name: string;
  number: string;
  street: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  city?: {
    id: string;
    name: string;
  } | null;
  country?: {
    id: string;
    name: string;
    code: string;
  } | null;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled(Box)({
  width: '100%',
  minHeight: '100vh',
  padding: '8px 16px',
  animation: `${fadeIn} 0.6s ease-out`,
});

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: 12,
  padding: 12,
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha('#00897B', 0.08)} 0%, ${alpha('#4CAF50', 0.05)} 100%)`,
  border: `2px solid ${alpha('#00897B', 0.2)}`,
  boxShadow: `0 8px 32px ${alpha('#00897B', 0.15)}`,
}));

const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: 12,
  padding: 12,
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha('#FFFFFF', 0.98)} 0%, ${alpha('#00897B', 0.03)} 100%)`,
  border: `2px solid ${alpha('#00897B', 0.15)}`,
  boxShadow: `0 4px 16px ${alpha('#00897B', 0.1)}`,
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: 16,
  right: 16,
  background: 'linear-gradient(135deg, #00897B 0%, #00695C 100%)',
  color: '#FFFFFF',
  boxShadow: `0 8px 32px ${alpha('#00897B', 0.4)}, 0 0 30px ${alpha('#00897B', 0.2)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #00695C 0%, #00897B 100%)',
    transform: 'scale(1.15) rotate(90deg)',
    boxShadow: `0 12px 48px ${alpha('#00897B', 0.6)}, 0 0 50px ${alpha('#00897B', 0.3)}`,
  },
}));

const ProjectsListing: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await DataService.get('api/Project/all');

      if (response.ok) {
        const responseData = await response.json();
        let rawProjects: any[] = [];

        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          rawProjects = responseData.data;
        } else if (Array.isArray(responseData)) {
          rawProjects = responseData;
        }

        const projects: Project[] = rawProjects.map(project => ({
          id: project.id,
          name: project.name,
          number: project.number,
          street: project.street,
          active: project.active,
          createdAt: project.createdAt || new Date().toISOString(),
          updatedAt: project.updatedAt || new Date().toISOString(),
          city: project.cityId ? {
            id: project.cityId,
            name: project.cityName || ''
          } : null,
          country: project.countryId ? {
            id: project.countryId,
            name: project.countryName || '',
            code: project.countryCode || ''
          } : null
        }));

        setProjects(projects);
        setFilteredProjects(projects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      dispatch(
        showAlert({
          message: 'Failed to load projects. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Apply filters
  useEffect(() => {
    let filtered = [...projects];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        project =>
          project.name.toLowerCase().includes(searchLower) ||
          project.number.toLowerCase().includes(searchLower) ||
          (project.street && project.street.toLowerCase().includes(searchLower)) ||
          project.city?.name.toLowerCase().includes(searchLower) ||
          project.country?.name.toLowerCase().includes(searchLower)
      );
    }

    // City filter
    if (filterCity) {
      filtered = filtered.filter(project => project.city?.name === filterCity);
    }

    // Country filter
    if (filterCountry) {
      filtered = filtered.filter(project => project.country?.name === filterCountry);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(project =>
        filterStatus === 'active' ? project.active : !project.active
      );
    }

    setFilteredProjects(filtered);
  }, [searchTerm, filterCity, filterCountry, filterStatus, projects]);

  // Get unique values for filters
  const uniqueCities = Array.from(new Set(projects.map(p => p.city?.name).filter(Boolean)));
  const uniqueCountries = Array.from(new Set(projects.map(p => p.country?.name).filter(Boolean)));

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterCity('');
    setFilterCountry('');
    setFilterStatus('all');
  };

  const activeFiltersCount = [filterCity, filterCountry].filter(Boolean).length +
    (filterStatus !== 'all' ? 1 : 0);

  // Handle Delete
  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/Project/${deleteId}`, {});
      if (response.ok) {
        fetchProjects();
        dispatch(
          showAlert({
            message: 'Project deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete project. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  // Handle Edit
  const handleEditClick = (project: Project) => {
    setSelectedProject(project);
    setEditDialogOpen(true);
  };

  // Handle Add
  const handleAddProject = () => {
    setSelectedProject(null);
    setEditDialogOpen(true);
  };

  // Handle Toggle Active
  const handleToggleActive = async (project: Project) => {
    setToggleLoading(project.id);

    try {
      const endpoint = project.active
        ? `api/Project/deactivate?id=${project.id}`
        : `api/Project/activate?id=${project.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        fetchProjects();
        dispatch(
          showAlert({
            message: `Project ${project.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update project status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update project status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // Handle Dialog Success
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    fetchProjects();
    dispatch(
      showAlert({
        message: selectedProject ? 'Project updated successfully!' : 'Project created successfully!',
        type: 'success'
      })
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            background: 'linear-gradient(135deg, #00897B 0%, #4CAF50 50%, #2196F3 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5,
          }}
        >
          Projects Management
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Organize and manage your projects efficiently
        </Typography>
      </PageHeader>

      {/* Search and Filter Controls */}
      <FilterSection>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: showFilters ? 1 : 0 }}>
          <TextField
            fullWidth
            placeholder="Search projects by name, number, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#A770EF' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                background: alpha('#FFFFFF', 0.8),
                '& fieldset': {
                  borderColor: alpha('#A770EF', 0.2),
                  borderWidth: 2,
                },
                '&:hover fieldset': {
                  borderColor: alpha('#A770EF', 0.4),
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#A770EF',
                },
              }
            }}
          />
          <IconButton
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              background: showFilters
                ? 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)'
                : `linear-gradient(135deg, ${alpha('#A770EF', 0.15)} 0%, ${alpha('#6E48AA', 0.1)} 100%)`,
              color: showFilters ? '#FFFFFF' : '#A770EF',
              border: `2px solid ${alpha('#A770EF', 0.3)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)',
                color: '#FFFFFF',
                transform: 'scale(1.1) rotate(180deg)',
                boxShadow: `0 4px 12px ${alpha('#A770EF', 0.4)}`,
              },
            }}
          >
            <FilterListIcon />
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  height: 20,
                  minWidth: 20,
                  background: 'linear-gradient(135deg, #00D9C0 0%, #00FFF0 100%)',
                  color: '#1A1A2E',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>City</InputLabel>
                  <Select
                    value={filterCity}
                    label="City"
                    onChange={(e) => setFilterCity(e.target.value)}
                  >
                    <MenuItem value="">All Cities</MenuItem>
                    {uniqueCities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={filterCountry}
                    label="Country"
                    onChange={(e) => setFilterCountry(e.target.value)}
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {uniqueCountries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="inactive">Inactive Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {activeFiltersCount > 0 && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={handleClearFilters}
                  size="small"
                  sx={{
                    color: '#FF5E78',
                    '&:hover': {
                      background: alpha('#FF5E78', 0.1),
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    Clear Filters
                  </Typography>
                </IconButton>
              </Box>
            )}
          </Box>
        </Collapse>
      </FilterSection>

      {/* Results Count */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Showing {filteredProjects.length} of {projects.length} projects
        </Typography>
      </Box>

      {/* Projects Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress
            sx={{
              color: '#A770EF',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
            size={60}
            thickness={4}
          />
        </Box>
      ) : filteredProjects.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha('#00897B', 0.05)} 0%, ${alpha('#4CAF50', 0.03)} 100%)`,
            border: `2px dashed ${alpha('#00897B', 0.2)}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 700 }}>
            No projects found
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Try adjusting your search or filters
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {filteredProjects.map((project) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
              <ProjectCard
                project={project}
                onEdit={() => handleEditClick(project)}
                onDelete={() => handleDeleteClick(project.id)}
                onToggleActive={() => handleToggleActive(project)}
                isToggling={toggleLoading === project.id}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Add Button */}
      <StyledFab
        onClick={handleAddProject}
        aria-label="add project"
      >
        <AddIcon sx={{ fontSize: 32 }} />
      </StyledFab>

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteId(null)}
      />

      {/* Add/Edit Project Dialog */}
      <ProjectDialog
        open={editDialogOpen}
        project={selectedProject}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </PageContainer>
  );
};

export default ProjectsListing;
